import { isGuestAccount } from '@shefira/shared';

import { listRoster } from '../net/account.js';
import { closeButton } from './hud.js';
import { uiText } from './text.js';
import './shop-modals.css';

/**
 * A guest account is found again by its token alone: signing out forgets it,
 * and every character on it with it. Two moments say so before it is too
 * late: the sign-out itself, and the end of the meadow's chain, when the
 * character has earned enough to be worth keeping (a level, a horse).
 */

/** The meadow chain's last quest: turned in at Sylvebois, the meadow behind. */
export const REGISTER_REMINDER_QUEST = 'meadow-west';

/** How the game opens the account tab; set by the world scene, absent on the roster. */
let openRegistration: (() => void) | undefined;

export function setRegistrationOpener(open: (() => void) | undefined): void {
  openRegistration = open;
}

/** Whether the session's account is still a guest one; a failed call says no, never blocks. */
export async function isGuestSession(token: string): Promise<boolean> {
  try {
    const roster = await listRoster(token);
    return isGuestAccount(roster);
  } catch {
    return false;
  }
}

type Choice = 'register' | 'confirm' | 'cancel';

interface ModalText {
  title: string;
  lead: string;
  /** The grey way out; absent when the window only offers to register. */
  confirm?: string;
  cancel: string;
}

function guestModal(text: ModalText): Promise<Choice> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'shop-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', uiText(text.title));

    const frame = document.createElement('section');
    frame.className = 'shop-modal__window';
    const title = document.createElement('h2');
    title.className = 'shop-modal__title';
    title.textContent = uiText(text.title);
    const lead = document.createElement('p');
    lead.className = 'shop-modal__hint';
    lead.textContent = uiText(text.lead);

    const actions = document.createElement('div');
    actions.className = 'shop-modal__actions';
    const button = (label: string, choice: Choice, danger = false): HTMLButtonElement => {
      const made = document.createElement('button');
      made.type = 'button';
      made.className = danger
        ? 'shop-modal__button shop-modal__button--danger'
        : 'shop-modal__button';
      made.textContent = uiText(label);
      made.addEventListener('click', () => {
        finish(choice);
      });
      return made;
    };
    if (openRegistration !== undefined) {
      actions.append(button('Enregistrer mon compte', 'register'));
    }
    if (text.confirm !== undefined) actions.append(button(text.confirm, 'confirm', true));
    actions.append(button(text.cancel, 'cancel'));

    const finish = (choice: Choice): void => {
      window.removeEventListener('keydown', onKeyDown, true);
      root.remove();
      resolve(choice);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      finish('cancel');
    };
    root.addEventListener('click', (event) => {
      if (event.target === root) finish('cancel');
    });

    frame.append(
      title,
      lead,
      actions,
      closeButton('Fermer (Échap)', () => {
        finish('cancel');
      }),
    );
    root.append(frame);
    document.body.append(root);
    window.addEventListener('keydown', onKeyDown, true);
  });
}

/**
 * The last word before a guest signs out. Resolves true when the player
 * still wants to leave; registering opens the account tab instead.
 */
export async function confirmGuestSignOut(): Promise<boolean> {
  const choice = await guestModal({
    title: 'Ton compte n’est pas enregistré',
    lead:
      openRegistration !== undefined
        ? 'Si tu te déconnectes maintenant, tes personnages disparaissent avec ce compte invité : ' +
          'rien ne permettra de les retrouver. Enregistre ton compte avec un e-mail, Google ou Steam pour les garder.'
        : 'Si tu te déconnectes maintenant, tes personnages disparaissent avec ce compte invité : ' +
          'rien ne permettra de les retrouver. Entre en jeu et enregistre ton compte (Profil, onglet Compte) pour les garder.',
    confirm: 'Me déconnecter quand même',
    cancel: 'Rester',
  });
  if (choice === 'register') openRegistration?.();
  return choice === 'confirm';
}

/** The nudge at the end of the meadow chain, only for a guest. */
export async function remindGuestToRegister(token: string): Promise<void> {
  const open = openRegistration;
  if (open === undefined || !(await isGuestSession(token))) return;
  const choice = await guestModal({
    title: 'Garde ton héros',
    lead:
      'La prairie est derrière toi, et ton personnage vit sur un compte invité : ' +
      's’il se déconnecte ou change de navigateur, il est perdu. ' +
      'Enregistre ton compte avec un e-mail, Google ou Steam pour le retrouver partout.',
    cancel: 'Plus tard',
  });
  if (choice === 'register') open();
}
